// The findings of one check, put in the shape the review box and panel show:
// one list (machine check, AI read, comparison), in document order per Pasal,
// each with its place, its sentences in both languages and the words to mark.
//
// Results from an older checker have no place or clause text: they still show,
// by row, with their English sentence, and the page suggests a Recheck.

export const LEVELS = ['HIGH', 'MED', 'LOW'];
export const SOURCES = ['machine', 'ai'];
const RANK = { HIGH: 0, MED: 1, LOW: 2 };

export function findingsOf(data) {
  if (!data) return [];
  const out = [];
  const add = (list, src, cmp) => (list || []).forEach((f, i) => out.push({
    key: f.key, stage: f.stage, raw: f, i: out.length + i,
    sev: f.severity, src, cmp, aiAgrees: !!(f.ai_note || f.translation_note), minor: !!f.minor,
    challenged: !!f.challenged, row: f.row, col: f.col,
    unit: f.unit, section: f.section, marks: f.marks || [], edit: f.edit || null,
    place: f.place, at: f.at, title: f.title, fix: f.fix, notes: f.notes_i18n || [],
    aiNote: f.ai_i18n || (f.ai_note ? { en: f.ai_note, id: f.ai_note } : null),
    defect: f.defect, quote: f.quote,
  }));
  add(data.rules && data.rules.findings, 'machine', false);
  add(data.ai && data.ai.findings, 'ai', false);
  add(data.translation && data.translation.findings, 'ai', true);
  return out;
}

// A machine finding the AI also made counts under ✦ AI as well.
export const matches = (f, level, source) => (level === 'all' || f.sev === level)
  && (source === 'all' || (source === 'ai' ? (f.src === 'ai' || f.aiAgrees) : f.src === source));

export const isOld = (data) => findingsOf(data).some((f) => !f.title);

// Per section in document order (unplaced last), most serious first within each.
export function ordered(findings, layout) {
  const pos = new Map(((layout && layout.sections) || []).map((s, i) => [s.key, i]));
  const sec = (f) => (f.section && pos.has(f.section) ? pos.get(f.section) : 1e6);
  return [...findings].sort((a, b) => sec(a) - sec(b)
    || RANK[a.sev] - RANK[b.sev]
    || String(a.unit || '~').localeCompare(String(b.unit || '~'))
    || a.i - b.i);
}

export const pick = (v, lang) => (v && (v[lang] || v.id || v.en || v.text)) || '';

export const placeOf = (f, lang, w) => pick(f.place, lang) || w.row(f.row);
export function atOf(f, lang, w) {
  const at = pick(f.at, lang);
  return at || placeOf(f, lang, w);
}
export const titleOf = (f, lang) => pick(f.title, lang) || f.defect || '';
export const fixOf = (f, lang) => pick(f.fix, lang);

// The heading of a section in the panel: "PASAL 3 · HARGA SEWA", and under it
// the other language, "Article 3 · Rent".
export function sectionHead(sec, lang) {
  const other = lang === 'id' ? 'en' : 'id';
  const title = (lg) => (sec.title && (sec.title[lg] || sec.title.text || sec.title[lg === 'id' ? 'en' : 'id'])) || '';
  const main = [pick(sec.label, lang), title(lang)].filter(Boolean).join(' · ');
  const sub = [pick(sec.label, other), title(other)].filter(Boolean).join(' · ');
  const bab = sec.bab && sec.bab.num ? `BAB ${sec.bab.num}${sec.bab.title ? ` ${sec.bab.title}` : ''}` : '';
  return { main: main.toUpperCase(), sub: sub !== main ? sub : '', bab };
}

// A clause's text cut into pieces: plain, marked, struck through with the
// replacement beside it, or a word to add. Only the words that change are
// struck (the checker's edit.ops), not the whole sentence the AI rewrote.
// Overlapping marks keep the first.
export function pieces(text, lang, marks, edit) {
  const ranges = [];
  (marks || []).forEach((m) => { if (m.lang === lang) ranges.push({ start: m.start, end: m.end, kind: 'mark' }); });
  if (edit && edit.lang === lang) {
    if (edit.ops && edit.ops.length) {
      edit.ops.forEach((o) => ranges.push(o.end > o.start
        ? { start: o.start, end: o.end, kind: 'del', to: o.to }
        : { start: o.start, end: o.start, kind: 'add', to: o.to }));
    } else {
      ranges.push({ start: edit.start, end: edit.end, kind: 'del', to: edit.to });
    }
  }
  ranges.sort((a, b) => a.start - b.start || (a.kind === 'mark' ? 1 : -1));
  const out = [];
  let at = 0;
  ranges.forEach((r) => {
    if (r.start < at || r.end > text.length || r.end < r.start || (r.end === r.start && r.kind !== 'add')) return;
    if (r.start > at) out.push({ kind: 'plain', text: text.slice(at, r.start) });
    out.push({ kind: r.kind, text: text.slice(r.start, r.end), to: r.to });
    at = r.end;
  });
  if (at < text.length) out.push({ kind: 'plain', text: text.slice(at) });
  return out;
}

// The edit list Legal can paste into a reply: per Pasal, each finding's place,
// level and the correction (or what is wrong, when there is no exact one).
export function editList(findings, layout, lang, w, fileName) {
  const lines = [`${fileName} · ${w.listTitle.toLowerCase()}`, ''];
  const secs = new Map(((layout && layout.sections) || []).map((s) => [s.key, s]));
  let current = null;
  findings.filter((f) => !f.minor && !f.challenged).forEach((f) => {
    const s = f.section && secs.get(f.section);
    const head = s ? sectionHead(s, lang).main : w.other.toUpperCase();
    if (head !== current) {
      if (current !== null) lines.push('');
      lines.push(head);
      current = head;
    }
    // The correction when there is one ("Tambahkan "11%" setelah "shall be""),
    // otherwise what is wrong.
    lines.push(`  - ${atOf(f, lang, w)} [${f.sev}] ${fixOf(f, lang) || titleOf(f, lang)}`);
  });
  return lines.join('\n');
}
